import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PRICE_TEMPLATE_FIELD,
  BasePriceRuleFormValue,
} from 'src/interfaces/Template.interface';
import { accessSessionTokenKeyName, loggedInAdminAccessToken } from 'src/utils/constants';

interface UsePriceModifierFieldsProps {
  platformId: number | null | undefined;
  enabled: boolean;
}

export type PriceRuleFormValue = BasePriceRuleFormValue & {
  ruleId?: number | null;
  destinationPlatformFieldDataId?: number | null;
};

export type PriceFieldForRules = Pick<
  PRICE_TEMPLATE_FIELD,
  'id' | 'api_name' | 'platform_field' | 'additional_platform_field_data'
> & {
  isPriceDate?: boolean
}

/** API path: field.additional_platform_field_data[0].description */
export const getPriceFieldDescription = (
  field: Pick<PRICE_TEMPLATE_FIELD, 'additional_platform_field_data'>
): string | null => {
  const value = field.additional_platform_field_data?.[0]?.description?.trim();
  return value || null;
};

export const generateDefaultPriceRules = (
  fields: PriceFieldForRules[]
): PriceRuleFormValue[] =>
  fields.map((field, index) => ({
    templateFieldId: field.id,
    priceType: field.api_name || 'Unknown',
    isPriceDate: Boolean(field.isPriceDate),
    adjustmentMode: 'FLAT' as const,
    adjustmentType: 'NO_CHANGE' as const,
    adjustmentValue: '',
    destinationPlatformFieldId: field.platform_field || null,
    mappingOption: 'DEFAULT' as const,
    defaultValue: '',
    mappedField: '' as number | '',
    startDate: '',
    endDate: '',
    ruleId: null,
    destinationPlatformFieldDataId: null,
    description: getPriceFieldDescription(field),
  }));

export const usePriceModifierFields = ({ platformId, enabled }: UsePriceModifierFieldsProps) => {
  const [priceTemplateFields, setPriceTemplateFields] = useState<PRICE_TEMPLATE_FIELD[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !platformId) {
      setPriceTemplateFields([]);
      return;
    }

    const controller = new AbortController();

    const fetchPriceFields = async () => {
      setIsLoading(true);
      try {
        const headers: Record<string, string> = {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        };
        const token = localStorage.getItem(accessSessionTokenKeyName);
        const adminToken = localStorage.getItem(loggedInAdminAccessToken);

        if (token) headers.Authorization = `Bearer ${token}`;
        if (adminToken) headers['xadmin'] = adminToken;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || ''}/platform-price-field`,
          {
            params: { platform_id: platformId },
            headers,
            signal: controller.signal,
          }
        );

        let priceTypes: PRICE_TEMPLATE_FIELD[] = [];
        if (Array.isArray(response?.data)) {
          priceTypes = response.data.filter(
            (item: any) => item.api_name && item.api_id && item.type
          ) as PRICE_TEMPLATE_FIELD[];
        } else if (Array.isArray(response?.data?.results)) {
          priceTypes = response.data.results.filter(
            (item: any) => item.api_name && item.api_id && item.type
          ) as PRICE_TEMPLATE_FIELD[];
        }

        if (priceTypes.length > 0) {
          priceTypes = priceTypes.filter(
            (pf) => pf.platform === platformId || pf.platform === Number(platformId)
          );
        }

        setPriceTemplateFields(priceTypes);
      } catch (error: any) {
        if (!axios.isCancel(error)) {
          console.error('Error loading price fields:', error);
          setPriceTemplateFields([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPriceFields();

    return () => {
      controller.abort();
    };
  }, [platformId, enabled]);

  return { priceTemplateFields, isLoading };
};
